import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { CalendarDays, ChevronLeft, ChevronRight, PhoneCall, DollarSign, XCircle, Mail, Copy, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import AppointmentLocations from './AppointmentLocations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const to12h = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const statusBadge = (s, refunded) => {
  if (s === 'confirmed') return <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">Confirmed</Badge>;
  if (s === 'pending_payment') return <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">Awaiting payment</Badge>;
  if (s === 'cancelled') return <Badge className="bg-red-500/20 text-red-300 border-red-500/30">{refunded ? 'Cancelled · refunded' : 'Cancelled'}</Badge>;
  return <Badge className="bg-white/10 text-slate-400 border-white/20">Expired</Badge>;
};

export default function OwnerAdminAppointments() {
  const today = new Date();
  const [monthDate, setMonthDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const monthKey = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/owner-admin/appointments?month=${monthKey}`, { credentials: 'include' });
      if (r.ok) setData(await r.json());
      else toast.error('Failed to load appointments');
    } catch { toast.error('Failed to load appointments'); }
    finally { setLoading(false); }
  }, [monthKey]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const cancelAppt = async (a) => {
    if (!window.confirm(`Cancel ${a.name}'s ${a.duration_minutes}-min call on ${a.date} at ${to12h(a.start_time)} ET? If they paid, refund from the Stripe dashboard.`)) return;
    const r = await fetch(`${API_URL}/api/owner-admin/appointments/${a.appointment_id}/cancel`, {
      method: 'POST', credentials: 'include',
    });
    if (r.ok) { toast.success('Appointment cancelled — slot freed'); fetchData(); }
    else toast.error('Cancel failed');
  };

  const appts = data?.appointments || [];
  const byDate = useMemo(() => {
    const m = {};
    appts.forEach((a) => { (m[a.date] = m[a.date] || []).push(a); });
    return m;
  }, [appts]);

  // Upcoming confirmed (this month view), soonest first
  const nowIso = new Date().toISOString();
  const upcoming = appts
    .filter((a) => a.status === 'confirmed' && a.end_utc > nowIso)
    .sort((a, b) => (a.start_utc > b.start_utc ? 1 : -1));

  // Calendar grid
  const firstDow = monthDate.getDay();
  const daysInMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const monthLabel = monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  return (
    <div className="space-y-6" data-testid="owner-admin-appointments">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-lg bg-emerald-500/10">
            <PhoneCall className="h-6 w-6 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Appointments</h1>
            <p className="text-sm text-muted-foreground">Pre-paid internal CPA Advisor calls · Mon–Fri 9am–4pm ET · $150/hr</p>
          </div>
        </div>
        <Button
          variant="outline"
          className="border-white/20 text-muted-foreground/80"
          onClick={() => {
            navigator.clipboard.writeText(`${window.location.origin}/book`);
            toast.success('Booking link copied — share it anywhere');
          }}
          data-testid="copy-booking-link-btn"
        >
          <Copy className="h-4 w-4 mr-2" /> Copy booking link
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-card border-white/15"><CardContent className="pt-6">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Upcoming (this month)</p>
          <p className="text-3xl font-bold text-white mt-1" data-testid="appt-upcoming-count">{data?.summary?.upcoming ?? 0}</p>
        </CardContent></Card>
        <Card className="bg-card border-white/15"><CardContent className="pt-6">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Confirmed (this month)</p>
          <p className="text-3xl font-bold text-white mt-1">{data?.summary?.confirmed ?? 0}</p>
        </CardContent></Card>
        <Card className="bg-card border-white/15"><CardContent className="pt-6">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Call revenue (this month)</p>
          <p className="text-3xl font-bold text-emerald-400 mt-1" data-testid="appt-revenue">
            ${(data?.summary?.revenue_confirmed ?? 0).toFixed(2)}
          </p>
        </CardContent></Card>
      </div>

      {/* Office locations manager — physical addresses clients can pick
        * when booking in-person meetings. Add / edit / retire from here. */}
      <AppointmentLocations />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Calendar */}
        <Card className="bg-card border-white/15 lg:col-span-3">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <CalendarDays className="h-4 w-4" /> {monthLabel}
            </CardTitle>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" data-testid="appt-prev-month"
                onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" data-testid="appt-next-month"
                onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-muted-foreground mb-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div key={d}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((d, i) => {
                if (d === null) return <div key={`e${i}`} />;
                const key = `${monthKey}-${String(d).padStart(2, '0')}`;
                const dayAppts = (byDate[key] || []).filter((a) => a.status === 'confirmed');
                const pend = (byDate[key] || []).filter((a) => a.status === 'pending_payment');
                return (
                  <div key={key}
                    className={`min-h-[72px] rounded-lg p-1.5 text-left ring-1 ${
                      key === todayKey ? 'ring-emerald-500/50 bg-emerald-500/5' : 'ring-white/10 bg-white/[0.02]'
                    }`}
                    data-testid={`cal-day-${key}`}
                  >
                    <p className="text-[11px] text-muted-foreground">{d}</p>
                    {dayAppts.slice(0, 2).map((a) => (
                      <div key={a.appointment_id} title={`${a.name} · ${to12h(a.start_time)} ET · ${a.duration_minutes}m`}
                        className="mt-0.5 truncate rounded bg-emerald-500/20 text-emerald-300 text-[10px] px-1 py-0.5">
                        {to12h(a.start_time)} {a.name.split(' ')[0]}
                      </div>
                    ))}
                    {dayAppts.length > 2 && <p className="text-[9px] text-emerald-400 mt-0.5">+{dayAppts.length - 2} more</p>}
                    {pend.length > 0 && <div className="mt-0.5 rounded bg-amber-500/15 text-amber-300 text-[9px] px-1">{pend.length} pending</div>}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming list */}
        <Card className="bg-card border-white/15 lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base">Upcoming calls</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              [...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 w-full bg-white/5" />)
            ) : upcoming.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No upcoming appointments this month.</p>
            ) : (
              upcoming.map((a) => (
                <div key={a.appointment_id} className="rounded-xl ring-1 ring-white/10 bg-white/[0.03] p-3"
                  data-testid={`upcoming-appt-${a.appointment_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-white font-medium">{a.name}</p>
                      <p className="text-xs text-emerald-300 mt-0.5">
                        {new Date(`${a.date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}{' '}
                        · {to12h(a.start_time)} ET · {a.duration_minutes} min
                      </p>
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                        <PhoneCall className="h-3 w-3" /> {a.phone}
                        <Mail className="h-3 w-3 ml-1" /> {a.email}
                        {a.email_bounced && (
                          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/40 gap-1"
                                 data-testid={`bounce-badge-${a.appointment_id}`}>
                            <AlertTriangle className="w-3 h-3" /> Email bounced — call them
                          </Badge>
                        )}
                      </p>
                      {a.notes && <p className="text-xs text-slate-400 mt-1 italic">“{a.notes}”</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-emerald-400 font-semibold text-sm flex items-center gap-0.5 justify-end">
                        <DollarSign className="h-3 w-3" />{a.amount.toFixed(2)}
                      </p>
                      <div className="mt-1">{statusBadge(a.status)}</div>
                      <Button variant="ghost" size="sm" onClick={() => cancelAppt(a)}
                        className="text-red-400/70 hover:text-red-400 h-7 mt-1 px-2"
                        data-testid={`cancel-appt-${a.appointment_id}`}>
                        <XCircle className="h-3.5 w-3.5 mr-1" /> Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* All bookings this month (incl pending/cancelled) */}
      <Card className="bg-card border-white/15">
        <CardHeader className="pb-3"><CardTitle className="text-white text-base">All bookings — {monthLabel}</CardTitle></CardHeader>
        <CardContent>
          {appts.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No bookings this month yet. Share your booking link!</p>
          ) : (
            <div className="space-y-1.5">
              {appts.map((a) => (
                <div key={a.appointment_id} className="flex items-center justify-between text-sm rounded-lg px-3 py-2 hover:bg-white/[0.03]">
                  <span className="text-slate-300 w-40">{a.date} · {to12h(a.start_time)}</span>
                  <span className="text-white flex-1 truncate">{a.name}</span>
                  <span className="text-muted-foreground w-16">{a.duration_minutes}m</span>
                  <span className="text-emerald-400 w-20 text-right">${a.amount.toFixed(2)}</span>
                  <span className="w-36 text-right">{statusBadge(a.status, a.refunded)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
